import { ProviderType } from './enums/reconciliation.enums';

export const RECONCILIATION_PATHS = {
  providers: 'providers',
  connections: 'connections',
  callback: 'callback',
  workspace: 'workspace',
} as const;

export const RECONCILIATION_ACTION_PERMISSION_NUMBERS = [
  74,
] as const;

export const RECONCILIATION_EXCEPTION_REASON_KEYS = [
  'RECONCILIATION.WORKSPACE.REASONS.BANK_FEE',
  'RECONCILIATION.WORKSPACE.REASONS.IOF',
  'RECONCILIATION.WORKSPACE.REASONS.BANK_ADJUSTMENT',
] as const;

export interface CredentialField {
  Key: string;
  Label: string;
  Type: 'password';
}

export interface StatusBadgeConfig {
  Label: string;
  Color: 'green' | 'yellow' | 'red' | 'gray';
}

export const PROVIDER_CREDENTIAL_FIELDS: Record<
  ProviderType,
  CredentialField[]
> = {
  [ProviderType.Pagarme]: [
    {
      Key: 'ApiKey',
      Label: 'RECONCILIATION.CREDENTIALS.API_KEY',
      Type: 'password',
    },
    {
      Key: 'ServiceRefererName',
      Label: 'RECONCILIATION.CREDENTIALS.SERVICE_REFERER_NAME',
      Type: 'password',
    },
  ],
  [ProviderType.Stone]: [
    {
      Key: 'ClientId',
      Label: 'RECONCILIATION.CREDENTIALS.CLIENT_ID',
      Type: 'password',
    },
    {
      Key: 'ClientSecret',
      Label: 'RECONCILIATION.CREDENTIALS.CLIENT_SECRET',
      Type: 'password',
    },
  ],
  [ProviderType.OpenFinance]: [
    {
      Key: 'ClientId',
      Label: 'RECONCILIATION.CREDENTIALS.CLIENT_ID',
      Type: 'password',
    },
    {
      Key: 'ClientSecret',
      Label: 'RECONCILIATION.CREDENTIALS.CLIENT_SECRET',
      Type: 'password',
    },
  ],
  [ProviderType.OFX]: [],
  [ProviderType.Manual]: [],
};

export const TENANT_CREDENTIAL_FIELDS: Record<
  ProviderType,
  CredentialField[]
> = {
  [ProviderType.Pagarme]: [
    {
      Key: 'SecretKey',
      Label: 'RECONCILIATION.CREDENTIALS.SECRET_KEY',
      Type: 'password',
    },
  ],
  [ProviderType.Stone]: [
    {
      Key: 'AccountId',
      Label: 'RECONCILIATION.CREDENTIALS.ACCOUNT_ID',
      Type: 'password',
    },
  ],
  [ProviderType.OpenFinance]: [],
  [ProviderType.OFX]: [],
  [ProviderType.Manual]: [],
};

export function providerTypeTranslationKey(type: ProviderType): string {
  switch (type) {
    case ProviderType.OpenFinance:
      return 'RECONCILIATION.PROVIDER_TYPE.OPEN_FINANCE';
    case ProviderType.OFX:
      return 'RECONCILIATION.PROVIDER_TYPE.OFX';
    case ProviderType.Stone:
      return 'RECONCILIATION.PROVIDER_TYPE.STONE';
    case ProviderType.Pagarme:
      return 'RECONCILIATION.PROVIDER_TYPE.PAGARME';
    case ProviderType.Manual:
      return 'RECONCILIATION.PROVIDER_TYPE.MANUAL';
  }
}

/**
 * Treasury `FinancialAccount.Type` values that support bank-reconciliation Open Finance flows.
 * Matches API `FinancialAccountType`: Bank = 1, Wallet = 2.
 */
export function isFinancialAccountTypeEligibleForBankConnections(
  accountType: number,
): boolean {
  return accountType === 1 || accountType === 2;
}

/** Type + non-empty id: required for keyed selects and `/connections` calls. */
export function isConnectableFinancialAccount(account: {
  FinancialAccountId: string;
  Type: number;
}): boolean {
  if (!String(account.FinancialAccountId ?? '').trim()) {
    return false;
  }
  return isFinancialAccountTypeEligibleForBankConnections(account.Type);
}

/** Bank-type only (`FinancialAccountType.Bank` = 1): bank connections account list. */
export function isBankFinancialAccountForConnections(account: {
  FinancialAccountId: string;
  Type: number;
}): boolean {
  if (!String(account.FinancialAccountId ?? '').trim()) {
    return false;
  }
  return account.Type === 1;
}
